import { useEffect, useRef, useState } from 'react';
import { Clock } from 'lucide-react';
import {
  formatDurationLabel,
  formatTimeLabel,
  hhmmToMinutes,
  minutesToHhmm,
  parseLooseTime,
} from '../timeParsing';
import { Sheet } from './Sheet';

/**
 * Friendlier replacement for a native `<input type="time">` (Dan: "the hour and time
 * picker thing is annoying"). Two ways in, same value:
 *
 * 1. Type loosely into the text field — "4pm", "4:30p", "930", "16:30" all parse (see
 *    `../timeParsing.ts` for the full grammar and am/pm inference rules). The field commits
 *    on blur/Enter, shows the parsed, friendly result ("4:00 PM"), and reverts on invalid
 *    input rather than accepting garbage.
 * 2. Tap the clock button for a scrollable sheet of every 15-minute increment, opened
 *    already scrolled to the current value's hour, each row a full 44px touch target.
 *
 * Pass `anchorMinutes` (the *start* time, in minutes-since-midnight) when this field is
 * picking an *end* time — every row then shows how long the event would be ("4:30 PM
 * (1h)"), and loose-typed ambiguous hours ("5") infer am/pm relative to the anchor instead
 * of the bare default heuristic.
 */
export interface TimeFieldProps {
  /** Current value as `"HH:MM"` (24h), matching `EventDraft.startTime`. */
  value: string;
  onChange: (hhmm: string) => void;
  label: string;
  testId?: string;
  /** Start time (minutes-since-midnight) when this field represents an end time. */
  anchorMinutes?: number;
}

const STEP_MINUTES = 15;
const ROW_COUNT = (24 * 60) / STEP_MINUTES;

export function TimeField({
  value,
  onChange,
  label,
  testId,
  anchorMinutes,
}: TimeFieldProps): JSX.Element {
  const committedMinutes = hhmmToMinutes(value);
  const [draftText, setDraftText] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  const displayText = draftText ?? formatTimeLabel(committedMinutes);

  const commit = (): void => {
    if (draftText === null) return;
    const parsed = parseLooseTime(draftText, { anchorMinutes });
    if (parsed !== null) onChange(minutesToHhmm(parsed));
    setDraftText(null);
  };

  useEffect(() => {
    if (!open) return;
    const row = listRef.current?.querySelector<HTMLElement>('[data-selected="true"]');
    row?.scrollIntoView({ block: 'center' });
  }, [open]);

  return (
    <div className="flex items-center gap-1.5">
      <input
        type="text"
        inputMode="text"
        value={displayText}
        aria-label={label}
        data-testid={testId}
        onFocus={() => setDraftText(formatTimeLabel(committedMinutes))}
        onChange={(e) => setDraftText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            commit();
            e.currentTarget.blur();
          } else if (e.key === 'Escape') {
            setDraftText(null);
            e.currentTarget.blur();
          }
        }}
        className="h-12 w-28 rounded-md border border-line bg-bg px-3 text-base text-fg outline-none focus:border-line-strong"
      />
      <button
        type="button"
        aria-label={`Choose ${label.toLowerCase()} from list`}
        data-testid={testId ? `${testId}-open` : undefined}
        onClick={() => setOpen(true)}
        className="flex h-12 w-11 items-center justify-center rounded-md border border-line bg-bg text-fg-2"
      >
        <Clock className="h-4 w-4" aria-hidden="true" />
      </button>

      <Sheet
        open={open}
        onClose={() => setOpen(false)}
        height="tall"
        label={label}
        testId={testId && `${testId}-sheet`}
      >
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <span className="text-sm font-semibold">{label}</span>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="h-11 px-1 text-sm text-accent"
          >
            Done
          </button>
        </div>
        <div ref={listRef} className="flex-1 overflow-y-auto" role="listbox" aria-label={label}>
          {Array.from({ length: ROW_COUNT }, (_, i) => i * STEP_MINUTES).map((minutes) => {
            const selected = minutes === committedMinutes;
            const forwardGap =
              anchorMinutes === undefined ? null : (minutes - anchorMinutes + 1440) % 1440 || 1440;
            return (
              <button
                key={minutes}
                type="button"
                role="option"
                aria-selected={selected}
                data-selected={selected}
                data-testid={testId && `${testId}-option-${minutesToHhmm(minutes)}`}
                onClick={() => {
                  onChange(minutesToHhmm(minutes));
                  setDraftText(null);
                  setOpen(false);
                }}
                className={`flex min-h-11 w-full items-center justify-between border-b border-line px-4 text-left text-sm last:border-b-0 ${
                  selected ? 'bg-raised font-semibold text-fg' : 'text-fg-2'
                }`}
              >
                <span>{formatTimeLabel(minutes)}</span>
                {forwardGap !== null && (
                  <span className="text-xs text-fg-3">({formatDurationLabel(forwardGap)})</span>
                )}
              </button>
            );
          })}
        </div>
      </Sheet>
    </div>
  );
}
